// ─── voicebox wire protocol ─────────────────────────────────────────────────
// The single contract between a voicebox session and whatever sits on the other
// end of the socket: our own face, an external app's SDK, or a test harness.
// Imported by BOTH sides so the types can never drift.
//
// One WebSocket carries two channels:
//   · TEXT frames   = JSON events (the discriminated unions below)
//   · BINARY frames = raw audio bytes in the format currently declared for that
//     direction — `session.ready.audio`, changed later by `audio.format`.
// Audio stays out of JSON so the hot path never pays base64.

export const PROTOCOL_VERSION = 1

export type Role = 'user' | 'assistant' | 'system'

/** Where the provider connection lives. The crux of the design — see docs/SERVICE_API.md. */
export type Placement = 'server' | 'client'

/** `vad` = provider detects turn ends; `ptt` = client declares them with `audio.commit`. */
export type TurnTaking = 'vad' | 'ptt'

export type AudioEncoding = 'pcm16' | 'opus' | 'mulaw'

export interface AudioFormat {
  encoding: AudioEncoding
  sampleRate: number
  channels: number
}

/** A word recognised from the assistant audio, positioned on that utterance's PCM clock. */
export interface CaptionWord {
  text: string
  startMs: number
  endMs: number
}

export const DEFAULT_AUDIO: AudioFormat = { encoding: 'pcm16', sampleRate: 24_000, channels: 1 }

/** Runtime guard for audio declarations received over the untyped WebSocket boundary. */
export function isAudioFormat(value: unknown): value is AudioFormat {
  if (!value || typeof value !== 'object') return false
  const audio = value as Record<string, unknown>
  return (
    (audio.encoding === 'pcm16' || audio.encoding === 'opus' || audio.encoding === 'mulaw') &&
    typeof audio.sampleRate === 'number' && Number.isFinite(audio.sampleRate) && audio.sampleRate > 0 &&
    typeof audio.channels === 'number' && Number.isInteger(audio.channels) && audio.channels > 0
  )
}

export type VisualSource = 'screen' | 'camera'
export type VisualFormat = 'image/jpeg'
export type VisualIntent = 'establish' | 'refresh' | 'focus' | 'event_candidate'

/** Whether the caller and model currently share a visual workspace. No pixels live here. */
export type VisualContextState =
  | { active: true; source: VisualSource }
  | { active: false; source?: VisualSource }

/** A provider-neutral still frame. Provider drivers translate it to their own wire. */
export interface VisualFrame {
  source: VisualSource
  format: VisualFormat
  /** Establishes/refreshes the workspace, or marks explicit/candidate attention. */
  intent?: VisualIntent
  /** Raw image bytes encoded as base64, without a data-URL prefix. */
  data: string
}

/** Limits a client must obey before it sends visual frames to this session. */
export interface VisualInputCapability {
  formats: VisualFormat[]
  /** Maximum base64 payload size, not decoded image size. */
  maxPayloadBytes: number
  /** Provider-supported ceiling. Clients may sample more slowly. */
  maxFramesPerSecond: number
}

export type ErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'provider_error'
  | 'tool_error'
  | 'unsupported'
  | 'expired'
  | 'internal'

/** Config handed to the client when it owns the provider connection. Never carries server secrets. */
export interface ClientProviderConfig {
  /** Provider id, so the SDK picks the matching client-side driver. */
  provider: string
  /** Provider-specific, JSON-serialisable. See each provider's `clientConfig()`. */
  [key: string]: unknown
}

// ── client → server ──────────────────────────────────────────────────────────
export type ClientEvent =
  /** First frame. Declares the protocol version the client speaks and its mic format. */
  | { t: 'hello'; v: number; audio?: AudioFormat }
  /** The client's input audio format changed mid-stream. */
  | { t: 'audio.format'; audio: AudioFormat }
  /** Push-to-talk: the user's turn is finished, respond now. */
  | { t: 'audio.commit' }
  /** Client-side VAD. `speech.start` doubles as the barge-in signal. */
  | { t: 'speech.start' }
  | { t: 'speech.stop' }
  /** A typed user turn (no audio). */
  | { t: 'text'; text: string }
  /** A camera or shared-screen frame for a provider with negotiated visual input. */
  | { t: 'visual.frame'; frame: VisualFrame }
  /** Starts or ends the shared visual workspace without pretending that state is a frame. */
  | { t: 'visual.context'; state: VisualContextState }
  /** Client-placed providers report what they heard/said back up to the session. */
  | { t: 'transcript'; role: Role; text: string; final: boolean }
  /** Result of a tool the server asked the client to run (`tool.call` with runAt:'client'). */
  | { t: 'tool.result'; callId: string; name: string; ok: boolean; result?: unknown; error?: string }
  /**
   * The other direction: a client-placed provider's model wants a tool the SESSION
   * owns (builtin like `deep_think`, or a caller webhook). The session dispatches it
   * and answers with `tool.result` carrying the same `callId`.
   */
  | { t: 'tool.call'; callId: string; name: string; args: unknown }
  /** Stop assistant output immediately. */
  | { t: 'cancel' }
  /** Change a provider-native idle timer; null disables it without ending the call. */
  | { t: 'idle.configure'; timeoutMs: number | null }
  /** Ask a composite provider to change its conversation mode. */
  | { t: 'provider.mode.set'; mode: string; params?: Record<string, unknown> }
  | { t: 'ping' }

// ── server → client ──────────────────────────────────────────────────────────
export type ServerEvent =
  /** Always the first server frame: everything the client needs to start streaming. */
  | {
      t: 'session.ready'
      v: number
      sessionId: string
      provider: string
      placement: Placement
      turnTaking: TurnTaking
      audio: { input: AudioFormat; output: AudioFormat }
      /** Tools the CLIENT must execute; every other tool is dispatched server-side. */
      clientTools: string[]
      /** Model-input capabilities negotiated from the active provider. */
      capabilities?: { visualInput?: VisualInputCapability }
      /** Present iff `placement === 'client'`. */
      clientConfig?: ClientProviderConfig
    }
  | { t: 'session.closed'; reason: string }
  | { t: 'audio.format'; dir: 'input' | 'output'; audio: AudioFormat }
  /** An upstream model call actually initiated by a server-placed provider. */
  | {
      t: 'runtime.stage'
      provider: string
      stage: 'realtime' | 'stt' | 'llm' | 'tts'
      model: string
      /** Start/end brackets make provider and network time visible without exposing content. */
      status?: 'start' | 'end'
      /** Provider-observed elapsed time. Present on `end`. */
      ms?: number
    }
  /** Lifecycle of a server-side provider socket recovery; the client call socket stays open. */
  | {
      t: 'runtime.recovery'
      provider: string
      status: 'start' | 'recovered' | 'failed'
      trigger: 'upstream_error' | 'upstream_close' | 'upstream_stall' | 'output_guard'
      attempt: number
      /** Recovery handshake elapsed time. Present after `start`. */
      ms?: number
    }
  /** A composite provider changed who owns turn detection and audio transmission. */
  | {
      t: 'input.mode'
      transmission: 'continuous' | 'gated'
      turnAuthority: 'provider' | 'client'
      reason?: 'hybrid_handoff' | 'visual_realtime' | 'provider_recovery' | 'consent_switch' | 'manual_switch'
    }
  /** A provider-confirmed product mode. Clients should update UI only after this event. */
  | {
      t: 'provider.mode'
      mode: string
      /** Why a composite changed mode; omitted for its initial state announcement. */
      reason?: 'provider_recovery' | 'consent_switch' | 'manual_switch'
    }
  /** Turn boundaries for both sides — drives barge-in and "is it talking" UI. */
  | { t: 'speech.start'; role: Role }
  | { t: 'speech.stop'; role: Role }
  | { t: 'transcript'; role: Role; text: string; final: boolean }
  /** Display-only captions derived from the actual output audio; never conversation history. */
  | { t: 'caption'; role: 'assistant'; words: CaptionWord[]; audioMs: number; final: boolean }
  /**
   * Text injected from outside the audio stream (`POST /v1/session/:id/say`).
   * A client-placed provider must feed this into its own model; a server-placed
   * one already has, so the client only needs to render it.
   */
  | { t: 'say'; role: Role; text: string }
  /**
   * Drop assistant output now — barge-in. The client must discard audio it has
   * buffered but not yet played; a client-placed provider must also stop generating.
   */
  | {
      t: 'cancel'
      /** Present when Voicebox rejected generated output rather than handling barge-in. */
      reason?: 'output_guard'
      /** Non-conversational classifier label for diagnostics; never contains model text. */
      detail?: string
    }
  | { t: 'idle.configured'; timeoutMs: number | null }
  | { t: 'tool.call'; callId: string; name: string; args: unknown; runAt: Placement }
  | { t: 'tool.result'; callId: string; name: string; ok: boolean; result?: unknown; error?: string; ms: number }
  | { t: 'error'; code: ErrorCode; message: string; fatal: boolean }
  | { t: 'pong' }

export type VoiceEvent = ClientEvent | ServerEvent

export function encode(ev: VoiceEvent): string {
  return JSON.stringify(ev)
}

/** Parses a TEXT frame. Returns null for anything that isn't a tagged event. */
export function decode<T extends VoiceEvent = VoiceEvent>(raw: string): T | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  return isTagged(parsed) ? (parsed as T) : null
}

function isTagged(v: unknown): v is { t: string } {
  return typeof v === 'object' && v !== null && typeof (v as { t?: unknown }).t === 'string'
}

/** Bytes per second of a format — used to size buffers and cap a turn's length. */
export function bytesPerSecond(f: AudioFormat): number {
  const bytesPerSample = f.encoding === 'pcm16' ? 2 : 1
  return f.sampleRate * f.channels * bytesPerSample
}
